import { Controller } from "@hotwired/stimulus"

import VoucherFooterController from "@/controllers/venue/voucher_footer_controller"
import * as Ga4 from "@/helpers/ga4_helper"
import { formatPrice } from "@/helpers/price_format"

const MINIMUM_VALUE = 25

export default class extends Controller {
  static targets = ["amountRadio", "customAmountInput"]
  declare readonly amountRadioTargets: HTMLInputElement[]
  declare readonly customAmountInputTarget: HTMLInputElement

  static outlets = ["voucher-footer"]
  declare readonly voucherFooterOutlet: VoucherFooterController
  declare readonly hasVoucherFooterOutlet: boolean

  static values = { selectedAmount: Number }
  declare selectedAmountValue: number

  voucherFooterOutletConnected(): void {
    this.voucherFooterOutlet.setPriceTarget(this.selectedAmountValue)
    this.voucherFooterOutlet.toggleEnabled(this.isMinimumAmount)
  }

  selectedAmountValueChanged(): void {
    // `voucherFooterOutlet` is always present in the DOM but there's a race condition because
    // its element is a child of this controller's element, so this controller connects first
    // before the outlet controller. But this value change callback fires immediately on
    // connect, so the outlet isn't ready at that point.
    if (this.hasVoucherFooterOutlet) {
      this.voucherFooterOutlet.setPriceTarget(this.selectedAmountValue)
      this.voucherFooterOutlet.toggleEnabled(this.isMinimumAmount)
    }
  }

  customAmountFocused(): void {
    // Selected amount is deemed to have changed when user focuses input
    this.selectedAmountValue = +this.numericalCustomAmountAsString
    this.deselectAmountRadios()
  }

  selectCustomAmount({ currentTarget }: IEvent<HTMLInputElement>): void {
    currentTarget.value = this.numericalCustomAmountAsString
    this.selectedAmountValue = +this.numericalCustomAmountAsString
  }

  selectRadioAmount({ currentTarget }: IEvent<HTMLInputElement>): void {
    // Clear custom amount before setting selected amount
    this.customAmountInputTarget.value = ""
    this.customAmountInputTarget.dispatchEvent(new Event("input", { bubbles: true }))

    this.selectedAmountValue = +currentTarget.value

    Ga4.selectContentEvent(
      "Monetary Voucher",
      formatPrice(this.selectedAmountValue),
      "Monetary Voucher Suggested Values"
    )
  }

  submitForm(): void {
    // The custom input has same `name` as radios so form submission works with either. But if a
    // radio is selected we'll end up with a duplicate param, so disable custom input if it
    // hasn't been used.
    if (!this.customAmountInputTarget.value) {
      this.customAmountInputTarget.disabled = true
    }

    Ga4.addToCartEvent(this.ecommerceItem, this.selectedAmountValue)
  }

  enableCustomInput(): void {
    // Re-enable customAmountInputTarget, as it might have been disabled after the form was submitted

    this.customAmountInputTarget.disabled = false
  }

  trackCustomAmount(): void {
    Ga4.pushGa4Event({ event: "custom_monetary_amount", price: this.selectedAmountValue })
  }

  private deselectAmountRadios(): void {
    this.amountRadioTargets.forEach((radio) => (radio.checked = false))
  }

  private get ecommerceItem(): Ga4.EcommerceItem {
    return {
      index: this.amountRadioTargets.findIndex((radio) => radio.checked),
      item_category: "Monetary Voucher",
      item_name: formatPrice(this.selectedAmountValue),
      price: this.selectedAmountValue,
      quantity: 1
    }
  }

  private get isMinimumAmount(): boolean {
    return this.selectedAmountValue >= MINIMUM_VALUE
  }

  private get numericalCustomAmountAsString(): string {
    // Disallow non-numerical input
    return this.customAmountInputTarget.value.replace(/[^\d]/g, "")
  }
}
